/**
 * 财务中心 - 内部调配页
 *
 * 面向财务角色在同服务商结算账户下两个记账子单元之间划转资金（一借一贷），页面结构：
 * 1. 调配表单：服务商下拉（必选）→ 联动加载该服务商下可调配子单元（本地绑定未关闭行+默认子单元，
 *    银行侧未绑定子单元不可选，与后端可调配范围一致）；付方/收方下拉校验付方不等于收方；
 *    金额必填大于 0 且最多两位小数；摘要选填不超过 42 字符
 * 2. 提交流程：前端校验拦截不发请求 → 二次确认弹窗展示付方/收方/金额 → 提交中按钮 loading
 *    防重复提交（银行处理中/结果未知由后端统一文案提示，前端透传展示）
 * 3. 成功提示含银行记账流水号（流水号取不到时展示业务参考号），成功后清空表单便于连续操作
 *
 * 空值口径：空值显示空白
 */
<template>
  <div class="finance-transfer-page">
    <el-breadcrumb separator-class="el-icon-arrow-right" class="page-breadcrumb">
      <el-breadcrumb-item>财务中心</el-breadcrumb-item>
      <el-breadcrumb-item>内部调配</el-breadcrumb-item>
    </el-breadcrumb>

    <el-card class="form-card" shadow="never">
      <div slot="header" class="card-header">
        <span>内部调配</span>
      </div>

      <el-form :model="form" label-width="130px" size="small" class="transfer-form">
        <el-form-item label="服务商" required>
          <el-select
            v-model="form.accountNumber"
            filterable
            clearable
            placeholder="请选择服务商"
            style="width: 320px"
            @change="handleProviderChange"
          >
            <el-option
              v-for="p in providerOptions"
              :key="p.accountNumber"
              :label="providerLabel(p)"
              :value="p.accountNumber"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="付方子单元" required>
          <el-select
            v-model="form.dmadbt"
            filterable
            clearable
            placeholder="请选择付方子单元"
            :loading="subunitLoading"
            style="width: 320px"
          >
            <el-option
              v-for="s in subunitOptions"
              :key="'pay-' + s.dmanbr"
              :label="subunitLabel(s)"
              :value="s.dmanbr"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="收方子单元" required>
          <el-select
            v-model="form.dmacrt"
            filterable
            clearable
            placeholder="请选择收方子单元"
            :loading="subunitLoading"
            style="width: 320px"
          >
            <el-option
              v-for="s in subunitOptions"
              :key="'recv-' + s.dmanbr"
              :label="subunitLabel(s)"
              :value="s.dmanbr"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="划转金额" required>
          <el-input
            v-model="form.amount"
            placeholder="单位：元，最多两位小数"
            maxlength="15"
            style="width: 320px"
          ></el-input>
        </el-form-item>
        <el-form-item label="摘要">
          <el-input
            v-model="form.trxtxt"
            placeholder="选填，不超过 42 个字符"
            maxlength="42"
            style="width: 320px"
          ></el-input>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :loading="submitting" @click="handleSubmit">提交调配</el-button>
        </el-form-item>
      </el-form>
    </el-card>
  </div>
</template>

<script>
import {
  getFinanceProviderList,
  getFinanceSubunitList,
  allocateFinanceTransfer
} from '@/api/userMG';

export default {
  name: 'FinanceTransfer',
  data() {
    return {
      // 服务商下拉选项
      providerOptions: [],
      // 可调配子单元下拉选项（随服务商联动加载）
      subunitOptions: [],
      subunitLoading: false,
      form: {
        accountNumber: '',
        dmadbt: '',
        dmacrt: '',
        amount: '',
        trxtxt: ''
      },
      submitting: false
    };
  },
  created() {
    this.loadProviders();
  },
  methods: {
    // 服务商下拉展示文案：账户名（账号）
    providerLabel(p) {
      return (p.accountName || '') + '（' + (p.accountNumber || '') + '）';
    },
    // 加载服务商下拉（招商银行记账子单元服务商账户）
    async loadProviders() {
      try {
        const res = await getFinanceProviderList();
        if (res && res.code === 0) {
          this.providerOptions = res.data || [];
        } else {
          this.$message.error((res && res.msg) || '服务商列表加载失败');
        }
      } catch (e) {
        // HTTP 400/500 由响应拦截器统一弹出后端 msg，此处仅兜底
      }
    },
    // 服务商切换后清空付收方选择并重新加载子单元下拉
    handleProviderChange() {
      this.form.dmadbt = '';
      this.form.dmacrt = '';
      this.subunitOptions = [];
      if (this.form.accountNumber) {
        this.loadSubunits();
      }
    },
    // 加载当前服务商下可调配子单元（本地绑定未关闭行+默认子单元，已关闭行与银行侧未绑定子单元不参与调配；
    // 接口响应为统一契约，下拉取 records，子单元数量天然一批内取完）
    async loadSubunits() {
      this.subunitLoading = true;
      try {
        const res = await getFinanceSubunitList({ accountNumber: this.form.accountNumber });
        if (res && res.code === 0) {
          const data = res.data || {};
          this.subunitOptions = (data.records || []).filter(s => s.defaultSubunit || (s.accountNumber && (s.status === 0 || s.status === 1)));
        } else {
          this.$message.error((res && res.msg) || '子单元列表加载失败');
        }
      } catch (e) {
        // HTTP 400/500 由响应拦截器统一弹出后端 msg，此处仅兜底
      } finally {
        this.subunitLoading = false;
      }
    },
    // 子单元下拉展示文案：名称（编号），默认子单元名称取不到时兜底展示
    subunitLabel(s) {
      const name = s.dmanam || (s.defaultSubunit ? '默认子单元' : '');
      return name + '（' + s.dmanbr + '）';
    },
    // 付方名称文案（二次确认弹窗用）
    subunitName(dmanbr) {
      const s = this.subunitOptions.find(item => item.dmanbr === dmanbr);
      if (!s) return dmanbr;
      return (s.dmanam || (s.defaultSubunit ? '默认子单元' : '')) + '（' + s.dmanbr + '）';
    },
    // 二次确认弹窗按 HTML 模式渲染，动态名称统一转义防注入
    escapeHtml(v) {
      return String(v === null || v === undefined ? '' : v)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    },
    // 提交前校验；返回空字符串表示通过，否则返回提示文案
    validateForm() {
      if (!this.form.accountNumber) return '请先选择服务商';
      if (!this.form.dmadbt) return '请选择付方子单元';
      if (!this.form.dmacrt) return '请选择收方子单元';
      if (this.form.dmadbt === this.form.dmacrt) return '付方与收方不能为同一子单元';
      const amount = (this.form.amount || '').trim();
      if (!amount) return '请输入划转金额';
      if (!/^\d+(\.\d{1,2})?$/.test(amount)) return '划转金额必须为大于 0 且最多两位小数的数字';
      if (parseFloat(amount) <= 0) return '划转金额必须大于 0';
      return '';
    },
    handleSubmit() {
      const error = this.validateForm();
      if (error) {
        this.$message.warning(error);
        return;
      }
      const amount = this.form.amount.trim();
      this.$confirm(
        '付方：' + this.escapeHtml(this.subunitName(this.form.dmadbt)) +
        '<br>收方：' + this.escapeHtml(this.subunitName(this.form.dmacrt)) +
        '<br>金额：' + amount + ' 元',
        '确认调配',
        {
          dangerouslyUseHTMLString: true,
          confirmButtonText: '确认',
          cancelButtonText: '取消',
          type: 'warning'
        }
      ).then(() => {
        this.doSubmit(amount);
      }).catch(() => {});
    },
    // 提交调配（提交中禁用按钮防重复提交；银行处理中/结果未知由后端文案透传提示）
    async doSubmit(amount) {
      this.submitting = true;
      try {
        const res = await allocateFinanceTransfer({
          accountNumber: this.form.accountNumber,
          dmadbt: this.form.dmadbt,
          dmacrt: this.form.dmacrt,
          amount: amount,
          trxtxt: (this.form.trxtxt || '').trim()
        });
        if (res && res.code === 0) {
          const data = res.data || {};
          const refNo = data.trxnbr || data.yurref || '';
          this.$alert('调配成功，银行记账流水号：' + refNo, '调配结果', {
            confirmButtonText: '知道了',
            type: 'success'
          });
          this.form.dmadbt = '';
          this.form.dmacrt = '';
          this.form.amount = '';
          this.form.trxtxt = '';
        } else {
          this.$message.error((res && res.msg) || '调配失败');
        }
      } catch (e) {
        // HTTP 400/500 由响应拦截器统一弹出后端 msg（含银行处理中/结果未知文案），此处仅兜底
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
.transfer-form {
  max-width: 560px;
}
</style>



// WEBPACK FOOTER //
// src/views/finance/transfer.vue